import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import {
  explainEncompassAccess,
  listEncompassPeople,
  setEncompassAutoSync,
  setEncompassVisibility,
  setEncompassVisibilityDefault,
} from "../encompassApi";
import {
  ACCESS_LABEL,
  ORG_DEFAULT,
  ORG_DEFAULT_COPY,
  SELECTION,
  widensAccess,
} from "../encompassConstants";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { EncompassPeopleTab, subjectKey } from "./EncompassPeopleTab";
import { OrgAvatar } from "./OrgAvatar";

/**
 * The Encompass side of the operations page: pick an organization, then decide
 * who sees which of its loans.
 *
 * One tab, deliberately. Encompass Partner Connect has no approval handshake,
 * no list endpoint and nothing to reconcile, and nothing is gated at import, so
 * there is no connection lifecycle to operate here — only the two questions
 * this screen answers.
 */
export function EncompassPanel({ env, organizationId, onClose }) {
  const [people, setPeople] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [savingDefault, setSavingDefault] = useState(false);
  const [savingAutoSync, setSavingAutoSync] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const loadPeople = useCallback(async () => {
    if (!organizationId) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const json = await listEncompassPeople(env, organizationId);
      if (!json?.success) {
        throw new Error(
          json?.reason || "Could not load this organization's people",
        );
      }

      setPeople(json.data);
    } catch (loadError) {
      setPeople(null);
      setError(
        loadError?.message || "Could not load this organization's people",
      );
    } finally {
      setLoading(false);
    }
  }, [env, organizationId]);

  useEffect(() => {
    setPeople(null);
    setError(null);
    loadPeople();
  }, [loadPeople]);

  /**
   * Run one write, keep the row that caused it busy, and reload from the server
   * afterwards rather than patching local state — the verdict on every other
   * row can move when an organization default changes.
   */
  const runAction = async (key, action, successMessage, setBusy) => {
    setBusy(key);
    try {
      const json = await action();
      if (!json?.success) {
        throw new Error(json?.reason || "That did not save");
      }

      toast.success(successMessage(json.data || {}));
      await loadPeople();
    } catch (actionError) {
      toast.error(actionError?.message || "That did not save");
    } finally {
      setBusy(null);
    }
  };

  const applyVisibility = (subject, selection) =>
    runAction(
      subjectKey(subject),
      () =>
        setEncompassVisibility(env, {
          organizationId,
          subjectType: subject.subjectType,
          memberId: subject.memberId || null,
          selection,
        }),
      () => "Saved.",
      setBusyKey,
    );

  // Only widening asks. Narrowing somebody is the safe direction, and
  // confirming it too would just train people to click through the dialog that
  // matters.
  //
  // With four powers "widening" is no longer one comparison. Moving somebody to
  // the team's loans widens a person limited to their own and narrows a person
  // who saw everything, and "follow the organization" can widen just as much as
  // granting a level outright — so the comparison is made properly rather than
  // special-cased on the top option.
  const handleSelect = (subject, selection) => {
    const orgDefault =
      people?.organization?.loanVisibilityDefault ||
      ORG_DEFAULT.FOLLOW_ADDY_ROLE;

    if (widensAccess(selection, subject, orgDefault)) {
      setConfirmAction({
        kind: "visibility",
        subject,
        selection,
        orgDefault,
        name: subject.name || subject.email || "this person",
      });

      return;
    }

    applyVisibility(subject, selection);
  };

  const applyDefault = (loanVisibilityDefault) =>
    runAction(
      "default",
      () =>
        setEncompassVisibilityDefault(env, {
          organizationId,
          loanVisibilityDefault,
        }),
      (data) =>
        data.changed === false
          ? "Already set to that."
          : `Saved. ${data.affected ?? 0} ${
              data.affected === 1 ? "person" : "people"
            } follow this.`,
      (value) => setSavingDefault(Boolean(value)),
    );

  // Widening the organization is the one change on this screen that can move
  // everybody at once, so it always confirms — and says how many.
  //
  // Only `follow_addy_role` widens: it is the one value that stops Encompass
  // deciding anything. The other two both narrow, and between themselves the
  // move is sideways rather than up.
  const handleChangeDefault = (loanVisibilityDefault) => {
    if (loanVisibilityDefault === people?.organization?.loanVisibilityDefault) {
      return;
    }

    if (loanVisibilityDefault === ORG_DEFAULT.FOLLOW_ADDY_ROLE) {
      setConfirmAction({ kind: "default", loanVisibilityDefault });

      return;
    }

    applyDefault(loanVisibilityDefault);
  };

  const applyAutoSync = (enabled) =>
    runAction(
      "auto-sync",
      () => setEncompassAutoSync(env, { organizationId, enabled }),
      () => (enabled ? "Auto-sync is on." : "Auto-sync is off."),
      (value) => setSavingAutoSync(Boolean(value)),
    );

  // Turning the feed on for an organization nothing is narrowing is exactly the
  // case that put a lender's whole pipeline on a customer's board, so it says so
  // before doing it rather than afterwards.
  const handleToggleAutoSync = (enabled) => {
    const wideOpen =
      people?.organization?.loanVisibilityDefault ===
      ORG_DEFAULT.FOLLOW_ADDY_ROLE;

    if (enabled && wideOpen) {
      setConfirmAction({ kind: "auto-sync", enabled });

      return;
    }

    applyAutoSync(enabled);
  };

  const handleExplain = async ({ subjectType, memberId, loan }) => {
    const json = await explainEncompassAccess(env, organizationId, {
      subjectType,
      memberId,
      loan,
    });
    if (!json?.success) {
      throw new Error(json?.reason || "Could not check access");
    }

    return json.data;
  };

  const runConfirmed = () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) {
      return;
    }

    if (action.kind === "visibility") {
      applyVisibility(action.subject, action.selection);
    } else if (action.kind === "default") {
      applyDefault(action.loanVisibilityDefault);
    } else if (action.kind === "auto-sync") {
      applyAutoSync(action.enabled);
    }
  };

  if (!organizationId) {
    return (
      <div className="integrations-card integrations-detail-panel">
        <div className="integrations-detail-empty">
          <div className="integrations-detail-empty-ico" aria-hidden="true">
            ⌕
          </div>
          <h3>No organization selected</h3>
          <p>Pick one to see who can open its Encompass loans.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="integrations-card integrations-detail-panel">
      <div className="integrations-detail-head">
        <div className="integrations-detail-id-row">
          <div className="integrations-org-identity">
            <OrgAvatar
              name={people?.organization?.name}
              id={organizationId}
              className="integrations-org-logo"
            />
            <div style={{ minWidth: 0 }}>
              <div className="integrations-org-name-row">
                <h2 className="integrations-org-name">
                  {people?.organization?.name || organizationId}
                </h2>
              </div>
              <div className="integrations-org-id integrations-mono">
                {organizationId}
                <button
                  type="button"
                  className="integrations-copy-id"
                  title="Copy Organization ID"
                  onClick={() => {
                    navigator.clipboard?.writeText(organizationId);
                    toast.success("Organization ID copied to clipboard.");
                  }}
                >
                  ⧉
                </button>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="integrations-btn integrations-btn-ghost"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      <div className="integrations-tab-panel">
        <EncompassPeopleTab
          people={people}
          loading={loading}
          error={error}
          busyKey={busyKey}
          disabled={loading}
          savingDefault={savingDefault}
          savingAutoSync={savingAutoSync}
          onSelect={handleSelect}
          onChangeDefault={handleChangeDefault}
          onToggleAutoSync={handleToggleAutoSync}
          onRetry={loadPeople}
          onExplain={handleExplain}
        />
      </div>

      <ConfirmationDialog
        open={Boolean(confirmAction)}
        title={confirmTitle(confirmAction)}
        message={confirmMessage(confirmAction, people)}
        confirmLabel={confirmLabel(confirmAction)}
        onConfirm={runConfirmed}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}

function confirmTitle(action) {
  if (action?.kind === "visibility") {
    return action.selection === SELECTION.ALL_LOANS
      ? "Show them every imported loan?"
      : "Show them more loans?";
  }
  if (action?.kind === "default") {
    return "Stop limiting everyone?";
  }

  return "Turn auto-sync on with nothing narrowing it?";
}

function confirmLabel(action) {
  if (action?.kind === "visibility") {
    return "Show more loans";
  }
  if (action?.kind === "default") {
    return "Stop limiting";
  }

  return "Turn it on";
}

function confirmMessage(action, people) {
  if (action?.kind === "visibility") {
    return `${action.name} will be able to open ${describeGrant(action)}, including loans they are not on.`;
  }
  if (action?.kind === "default") {
    return "Everyone without a decision of their own will fall back to whatever their Addy role allows. In this organization that means anyone who can open every loan will see every loan Encompass delivered — including other branches, if this lender's instance covers more than this organization.";
  }

  const name = people?.organization?.name || "This organization";

  return `${name} will start accepting every loan an org-wide Encompass rule orders, and nothing on this screen is narrowing who sees them. Set the default to "only loans they are on" first if this lender's instance is wider than this organization.`;
}

/**
 * What a pending grant will let somebody open, in the same words the row and
 * the control use.
 *
 * "Follow the organization" has no words of its own, so it borrows the
 * organization default's — the operator is confirming an effect, not a setting.
 *
 * @param {Object} action
 * @returns {string}
 */
function describeGrant(action) {
  const bySelection = {
    [SELECTION.ALL_LOANS]: ACCESS_LABEL.all,
    [SELECTION.TEAM_LOANS]: ACCESS_LABEL.team,
    [SELECTION.OWN_LOANS]: ACCESS_LABEL.own,
  };

  const label =
    bySelection[action.selection] ||
    ORG_DEFAULT_COPY[action.orgDefault]?.label ||
    "whatever their Addy role allows";

  return label.toLowerCase();
}
